/**
 * useAnalytics - centrale GA4 event tracking voor Plug2Charge
 *
 * Gebruik:
 *   import { useAnalytics } from '@composables/useAnalytics'
 *   const { trackEvent, trackAddToCart, trackViewItem, ... } = useAnalytics()
 *
 * Alle functies zijn no-ops als gtag niet beschikbaar is (SSR, adblockker, etc.).
 */

// ── Helper ────────────────────────────────────────────────────────────────────
function gtag(...args) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return
  window.gtag(...args)
}

/**
 * Zet een WooCommerce product-object om naar GA4 item-formaat.
 */
function toItem(product, { quantity = 1, index = undefined } = {}) {
  if (!product) return null
  const item = {
    item_id:       String(product.id || product.sku || ''),
    item_name:     product.name || '',
    price:         parseFloat(product.price || product.regular_price || 0),
    item_category: product.categories?.[0]?.name || '',
    item_category2: product.categories?.[1]?.name || undefined,
    quantity,
  }
  if (index !== undefined) item.index = index
  // Merk uit product-attributen
  const merkAttr = product.attributes?.find(a =>
    a.slug === 'pa_merk' || a.slug === 'merk' || a.name?.toLowerCase() === 'merk'
  )
  if (merkAttr?.options?.[0]) item.item_brand = merkAttr.options[0]

  // Schoon undefined-waarden op (GA4 houdt niet van undefined)
  return Object.fromEntries(Object.entries(item).filter(([, v]) => v !== undefined))
}

// ── Geëxporteerde composable ──────────────────────────────────────────────────
export function useAnalytics() {

  /** Generiek event - gebruik dit als fallback voor alles wat geen eigen helper heeft */
  const trackEvent = (eventName, params = {}) => {
    gtag('event', eventName, params)
  }

  // ── Ecommerce: product-lijsten ─────────────────────────────────────────────

  /**
   * Lijst van producten in beeld (producten-pagina, categorie-pagina, etc.)
   * @param {Array}  products     - Array van product-objecten
   * @param {string} listName     - Naam van de lijst ('Alle producten', 'Zonnepanelen', etc.)
   * @param {string} [listId]     - Optionele machine-id voor de lijst
   */
  const trackViewItemList = (products, listName = 'Productoverzicht', listId = '') => {
    if (!products?.length) return
    gtag('event', 'view_item_list', {
      item_list_name: listName,
      item_list_id:   listId,
      items: products.slice(0, 40).map((p, i) => toItem(p, { index: i })),
    })
  }

  /**
   * Gebruiker klikt op een product-kaart (navigeert naar detail).
   * @param {Object} product
   * @param {string} listName  - Lijst waar het product in stond
   * @param {number} [index]   - Positie in de lijst (0-based)
   */
  const trackSelectItem = (product, listName = 'Productoverzicht', index = undefined) => {
    if (!product) return
    gtag('event', 'select_item', {
      item_list_name: listName,
      items: [toItem(product, { index })],
    })
  }

  // ── Ecommerce: product-detail ──────────────────────────────────────────────

  /**
   * Product-detailpagina bekeken.
   */
  const trackViewItem = (product) => {
    if (!product) return
    gtag('event', 'view_item', {
      currency: 'EUR',
      value:    parseFloat(product.price || 0),
      items:    [toItem(product)],
    })
  }

  // ── Ecommerce: winkelwagen ─────────────────────────────────────────────────

  /**
   * Product toegevoegd aan winkelwagen.
   * @param {Object} product
   * @param {number} quantity
   * @param {string} [source] - 'product-detail' | 'product-card' | 'bundle' | 'grouped' | 'quick-add'
   */
  const trackAddToCart = (product, quantity = 1, source = 'product-detail') => {
    if (!product) return
    gtag('event', 'add_to_cart', {
      currency: 'EUR',
      value:    parseFloat(product.price || 0) * quantity,
      items:    [toItem(product, { quantity })],
      source,
    })
  }

  /**
   * Product verwijderd uit winkelwagen.
   */
  const trackRemoveFromCart = (product, quantity = 1) => {
    if (!product) return
    gtag('event', 'remove_from_cart', {
      currency: 'EUR',
      value:    parseFloat(product.price || 0) * quantity,
      items:    [toItem(product, { quantity })],
    })
  }

  /**
   * Winkelwagen-pagina bekeken.
   * @param {Array}  items   - Cart items (met .product + .quantity)
   * @param {number} total   - Totaalbedrag
   */
  const trackViewCart = (items = [], total = 0) => {
    gtag('event', 'view_cart', {
      currency: 'EUR',
      value:    parseFloat(total),
      items:    items.map((ci, i) => toItem(ci.product || ci, { quantity: ci.quantity || 1, index: i })).filter(Boolean),
    })
  }

  // ── Ecommerce: checkout-funnel ────────────────────────────────────────────

  /**
   * Checkout gestart (begin_checkout event).
   * @param {Array}  items  - Cart items
   * @param {number} total  - Totaalbedrag
   */
  const trackBeginCheckout = (items = [], total = 0) => {
    gtag('event', 'begin_checkout', {
      currency: 'EUR',
      value:    parseFloat(total),
      items:    items.map((ci, i) => toItem(ci.product || ci, { quantity: ci.quantity || 1, index: i })).filter(Boolean),
    })
  }

  /**
   * Aankoop voltooid (purchase event) - fire op de thank-you pagina.
   * @param {Object} order        - { id, number, total, email }
   * @param {Array}  items        - Array van gekochte items (zelfde formaat als cart items)
   * @param {number} shippingCost - Verzendkosten
   */
  const trackPurchase = (order, items = [], shippingCost = 0) => {
    if (!order?.id) return

    // Enhanced conversions (in-page): geef first-party klantdata mee zodat Google
    // Ads conversies beter kan matchen. gtag hasht dit client-side voor verzending.
    const userData = {}
    if (order.email) userData.email = order.email
    if (order.phone) userData.phone_number = order.phone
    const addr = {}
    if (order.firstName) addr.first_name  = order.firstName
    if (order.lastName)  addr.last_name   = order.lastName
    if (order.postcode)  addr.postal_code = order.postcode
    if (order.city)      addr.city        = order.city
    if (order.country)   addr.region      = order.country
    if (Object.keys(addr).length)     userData.address = addr
    if (Object.keys(userData).length) gtag('set', 'user_data', userData)

    // GA4 purchase event
    gtag('event', 'purchase', {
      transaction_id: String(order.number || order.id),
      value:          parseFloat(order.total || 0),
      tax:            parseFloat((parseFloat(order.total || 0) * 0.21 / 1.21).toFixed(2)),
      shipping:       parseFloat(shippingCost),
      currency:       'EUR',
      items:          items.map((ci, i) => toItem(ci.product || ci, { quantity: ci.quantity || 1, index: i })).filter(Boolean),
    })

    // Google Ads conversie - aankoop
    gtag('event', 'conversion', {
      send_to:        'AW-17799109734/M8qfCOufo9AbEOa4o6dC',
      value:          parseFloat(order.total || 0),
      currency:       'EUR',
      transaction_id: String(order.number || order.id),
    })
  }

  // ── Ecommerce: verlanglijst ────────────────────────────────────────────────

  /**
   * Product aan verlanglijst toegevoegd of verwijderd.
   */
  const trackWishlist = (product, action = 'add') => {
    if (!product) return
    if (action === 'add') {
      gtag('event', 'add_to_wishlist', {
        currency: 'EUR',
        value:    parseFloat(product.price || 0),
        items:    [toItem(product)],
      })
    } else {
      gtag('event', 'remove_from_wishlist', {
        items: [toItem(product)],
      })
    }
  }

  // ── Zoeken & filteren ──────────────────────────────────────────────────────

  /**
   * Zoekopdracht ingevoerd.
   * @param {string} query          - Zoekterm
   * @param {number} [resultsCount] - Aantal resultaten
   */
  const trackSearch = (query, resultsCount = undefined) => {
    if (!query?.trim()) return
    const params = { search_term: query.trim() }
    if (resultsCount !== undefined) params.results_count = resultsCount
    gtag('event', 'search', params)
  }

  /**
   * Categorie-filter toegepast.
   */
  const trackFilterCategory = (categoryName, categoryId) => {
    gtag('event', 'filter_category', {
      category_name: categoryName || '',
      category_id:   categoryId || '',
    })
  }

  /**
   * Attribuut-filter toegepast of verwijderd.
   * @param {string} attributeName - Naam van attribuut (bijv. "Vermogen")
   * @param {string} termName      - Gekozen waarde (bijv. "400W")
   * @param {'apply'|'clear'} action
   */
  const trackFilterAttribute = (attributeName, termName, action = 'apply') => {
    gtag('event', 'filter_attribute', {
      attribute_name: attributeName || '',
      term_name:      termName || '',
      action,
    })
  }

  /**
   * Alle filters gewist.
   */
  const trackClearFilters = () => {
    gtag('event', 'clear_filters')
  }

  /**
   * Sorteervolgorde gewijzigd.
   * @param {string} sortValue - bijv. 'price', 'price-desc', 'title', 'date'
   */
  const trackSortChange = (sortValue) => {
    gtag('event', 'sort_change', { sort_by: sortValue })
  }

  /**
   * Weergavemodus gewijzigd (grid ↔ lijst).
   */
  const trackViewModeToggle = (mode) => {
    gtag('event', 'view_mode_toggle', { view_mode: mode })
  }

  // ── Leads / conversies ─────────────────────────────────────────────────────

  /**
   * Offerte-aanvraagformulier ingediend.
   * @param {string} productName - Naam van het product waarvoor offerte aangevraagd wordt
   */
  const trackQuoteRequest = (productName = '') => {
    gtag('event', 'generate_lead', {
      lead_type:    'quote_request',
      product_name: productName,
      currency:     'EUR',
    })
  }

  /**
   * Voorraad-notificatie aangevraagd ("Mail mij bij voorraad").
   */
  const trackStockNotify = (productName = '', productId = '') => {
    gtag('event', 'generate_lead', {
      lead_type:    'stock_notify',
      product_name: productName,
      product_id:   String(productId),
    })
  }

  // ── Account ────────────────────────────────────────────────────────────────

  /**
   * Inloggen geslaagd.
   * @param {string} method - 'email' | 'google' | etc.
   */
  const trackLogin = (method = 'email') => {
    gtag('event', 'login', { method })
  }

  /**
   * Registratie geslaagd.
   */
  const trackSignUp = (method = 'email') => {
    gtag('event', 'sign_up', { method })
  }

  // ── Navigatie / content ────────────────────────────────────────────────────

  /**
   * Handmatig page_view sturen (voor SPA-navigatie).
   * De router roept dit automatisch aan - niet nodig in individuele pagina's.
   */
  const trackPageView = (path, title) => {
    gtag('event', 'page_view', {
      page_path:  path,
      page_title: title || document.title,
    })
  }

  /**
   * FAQ-item openengeklapt.
   */
  const trackFaqExpand = (question = '') => {
    gtag('event', 'faq_expand', { question })
  }

  /**
   * Verlanglijst gedeeld.
   */
  const trackShareWishlist = (method = 'link') => {
    gtag('event', 'share', {
      method,
      content_type: 'wishlist',
    })
  }

  /**
   * Merk-pagina bekeken.
   */
  const trackViewBrand = (brandName = '') => {
    gtag('event', 'view_brand', { brand_name: brandName })
  }

  /**
   * Categorie-pagina bekeken.
   */
  const trackViewCategory = (categoryName = '', categorySlug = '') => {
    gtag('event', 'view_category', {
      category_name: categoryName,
      category_slug: categorySlug,
    })
  }

  return {
    trackEvent,
    // Ecommerce
    trackViewItemList,
    trackSelectItem,
    trackViewItem,
    trackAddToCart,
    trackRemoveFromCart,
    trackViewCart,
    trackBeginCheckout,
    trackPurchase,
    trackWishlist,
    // Zoeken & filteren
    trackSearch,
    trackFilterCategory,
    trackFilterAttribute,
    trackClearFilters,
    trackSortChange,
    trackViewModeToggle,
    // Leads
    trackQuoteRequest,
    trackStockNotify,
    // Account
    trackLogin,
    trackSignUp,
    // Navigatie
    trackPageView,
    trackFaqExpand,
    trackShareWishlist,
    trackViewBrand,
    trackViewCategory,
  }
}
